import { Observable, Subject, BehaviorSubject, combineLatest, map } from 'rxjs';
import { TradingEventsPort, AuctionStarted, AuctionExtended, AuctionEnded, BidAccepted, TradingProcedureStatusChanged, BidHistoryCreated, ConnectionStatus } from '@tradgine/application-english-auction-trade';
import { toTradingProcedureStatus } from '../mappers/mappers';
import { inject } from '@angular/core';
import {
    HubConnection,
    HttpTransportType,
    HubConnectionBuilder,
    IHttpConnectionOptions,
    LogLevel,
} from '@microsoft/signalr';
import { SessionData } from '../../../auth/models/session-data.model';
import { APP_CONFIG } from '../../../../env/env.token';
import {
    TradingProcedureReportingHubService
} from '../../../../core/hubs/trading-procedure-reporting-hub/trading-procedure-reporting.hub.service';
import { BidHistoryReportingHubService } from '../../../../core/hubs/bid-history-reporting-hub/bid-history-reporting-hub.service';
import { LocalStorageService } from '../../../../core/services/browser-storage/local-storage.service';
import { LocalStorageKeys } from '../../../../core/services/browser-storage/types/local-storage.enum';

export class TradingEventsAdapter implements TradingEventsPort {
    get bidAccepted$(): Observable<BidAccepted> {
        return this._bidAccepted$.asObservable();
    }
    private _bidAccepted$ = new Subject<BidAccepted>();

    get tradingProcedureStatusChanged$(): Observable<TradingProcedureStatusChanged> {
        return this.tradingProcedureStatusReportingHubService.tradingProcedureStatusChanged$.pipe(
            map((msg) => ({
                id: msg.id,
                status: toTradingProcedureStatus(msg.status),
            }))
        );
    }

    get auctionEnded$(): Observable<AuctionEnded> {
        return this._auctionEnded$.asObservable();
    }
    private _auctionEnded$ = new Subject<AuctionEnded>();

    get auctionExtended$(): Observable<AuctionExtended> {
        return this._auctionExtended$.asObservable();
    }
    private _auctionExtended$ = new Subject<AuctionExtended>();

    get auctionStarted$(): Observable<AuctionStarted> {
        return this._auctionStarted$.asObservable();
    }
    private _auctionStarted$ = new Subject<AuctionStarted>();

    get reconnected$(): Observable<void> {
        return this._reconnected$.asObservable();
    }
    private _reconnected$ = new Subject<void>();

    get connectionStatus$(): Observable<ConnectionStatus> {
        return this._connectionStatus$;
    }
    private _bidHubStatus$ = new BehaviorSubject<ConnectionStatus>('connecting');
    private _auctionHubStatus$ = new BehaviorSubject<ConnectionStatus>('connecting');
    private _connectionStatus$ = combineLatest([this._bidHubStatus$, this._auctionHubStatus$]).pipe(
        map(([bid, auction]): ConnectionStatus => {
            if (bid === 'disconnected' && auction === 'disconnected') return 'disconnected';
            if (bid === 'reconnecting' || auction === 'reconnecting') return 'reconnecting';
            if (bid === 'connecting' || auction === 'connecting') return 'connecting';
            return 'connected';
        })
    );

    private readonly localStorage = inject(LocalStorageService);
    private readonly appConfig = inject(APP_CONFIG);
    private readonly tradingProcedureStatusReportingHubService = inject(TradingProcedureReportingHubService);
    private readonly bidHistoryReportingHubService = inject(BidHistoryReportingHubService);

    get bidHistoryCreated$(): Observable<BidHistoryCreated> {
        return this.bidHistoryReportingHubService.bidHistoryCreated$;
    }

    private readonly bidReportConnection: HubConnection;
    private readonly auctionReportConnection: HubConnection;

    constructor() {
        const options: IHttpConnectionOptions = {
            transport: HttpTransportType.WebSockets,
            skipNegotiation: true,
            accessTokenFactory: () => this.localStorage.getItem<SessionData>(LocalStorageKeys.SessionData)?.accessToken,
        };

        const logLevel = this.appConfig.environment === 'Production' ? LogLevel.Error : LogLevel.Information;

        this.bidReportConnection = new HubConnectionBuilder()
            .withUrl(this.appConfig.bidReportingHubUrl, options)
            .withAutomaticReconnect()
            .configureLogging(logLevel)
            .build();

        this.auctionReportConnection = new HubConnectionBuilder()
            .withUrl(this.appConfig.auctionReportingHubUrl, options)
            .withAutomaticReconnect()
            .configureLogging(logLevel)
            .build();

        // Connection lifecycle hooks — регистрируются один раз в конструкторе
        this.bidReportConnection.onreconnecting(() => this._bidHubStatus$.next('reconnecting'));
        this.bidReportConnection.onreconnected(() => {
            this._bidHubStatus$.next('connected');
            this._reconnected$.next();
        });
        this.bidReportConnection.onclose(() => this._bidHubStatus$.next('disconnected'));

        this.auctionReportConnection.onreconnecting(() => this._auctionHubStatus$.next('reconnecting'));
        this.auctionReportConnection.onreconnected(() => {
            this._auctionHubStatus$.next('connected');
            this._reconnected$.next();
        });
        this.auctionReportConnection.onclose(() => this._auctionHubStatus$.next('disconnected'));

        // Регистрируем обработчики событий один раз в конструкторе
        this.registerHandlers();
    }

    stopConnections(): void {
        this.bidReportConnection.stop();
        this.auctionReportConnection.stop();
    }

    startConnections() {
        this._bidHubStatus$.next('connecting');
        this._auctionHubStatus$.next('connecting');

        this.bidReportConnection
            .start()
            .then(() => this._bidHubStatus$.next('connected'))
            .catch((err) => {
                console.log(err);
                this._bidHubStatus$.next('disconnected');
            });

        this.auctionReportConnection
            .start()
            .then(() => this._auctionHubStatus$.next('connected'))
            .catch((err) => {
                console.log(err);
                this._auctionHubStatus$.next('disconnected');
            });
    }

    private registerHandlers(): void {
        this.bidReportConnection.on('ReportBidAccepted', (args) => {
            this._bidAccepted$.next({
                auctionId: args.auctionId,
                organizationId: args.organizationId,
                iteration: args.iteration,
            });
        });

        this.auctionReportConnection.on('ReportAuctionStarted', (args) => {
            this._auctionStarted$.next({ id: args.id });
        });

        this.auctionReportConnection.on('ReportAuctionEnded', (args) => {
            this._auctionEnded$.next({ id: args.id });
        });

        this.auctionReportConnection.on('ReportAuctionExtended', (args) => {
            this._auctionExtended$.next({
                id: args.id,
                endDateTime: args.endDateTime,
            });
        });
    }
}
